import React, { useEffect, useState } from "react";
/* Uhamiaji wa tuli unaokusudiwa hapa: SessionProvider inaanzisha AUTH/MUMUUZI mapema katika maisha ya programu */
/* eslint-disable-next-line no-restricted-imports -- SessionProvider lazima ianzishe uthibitisho kwa sinkrono */
import { supabase } from "@/lib/supabase/client";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "@/components/ui/use-toast";
import type { User } from "@supabase/supabase-js";
import { setSentryUser } from "@/lib/sentry";
import { SessionContext } from "@/contexts/SessionContext";
import {
  generateCsrfToken,
  getCsrfToken,
  clearCsrfToken,
  startSecureSession,
  endSecureSession,
  logSecurityEvent,
  registerKnownDevice,
  isKnownDevice,
} from "@/lib/security";
import { computePostLoginRedirect, enrichUserWithProfileRoles } from "@/lib/auth/redirect";
import { extractUserRoles, ADMIN_ROLES, isAdminUser, getAdminSelectedZone, setAdminSelectedZone as persistAdminZone, clearAdminSelectedZone, type AdminZone } from "@/lib/rbac";
import {
  createSession,
  endSession,
  updateSessionActivity,
  generateDeviceFingerprint,
  getUserSessions,
  recordAnomaly,
} from "@/lib/accountIntegrity";
import { sessionManager, configureSessionPersistence } from "@/lib/sessionManagement";
import { ROLES } from "@/lib/roles";
import { InitialLoadingScreen } from "@/components/shell/InitialLoadingScreen";

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [csrfToken, setCsrfToken] = useState<string | null>(null);
  const [currentSessionToken, setCurrentSessionToken] = useState<string | null>(null);
  const [demoModeEnabled, setDemoModeEnabled] = useState(false);
  const [demoModeLoading, setDemoModeLoading] = useState(false);
  const [adminSelectedZone, setAdminZone] = useState<AdminZone>(getAdminSelectedZone);
  const navigate = useNavigate();
  const location = useLocation();

  // Callback for changing admin zone — persists to localStorage and navigates
  const handleSetAdminZone = (zone: AdminZone) => {
    setAdminZone(zone);
    persistAdminZone(zone);
  };

  // Track session activity periodically
  useEffect(() => {
    if (!currentSessionToken) return;

    const activityInterval = setInterval(() => {
      updateSessionActivity(currentSessionToken).catch(console.error);
    }, 60000); // Update every minute

    return () => clearInterval(activityInterval);
  }, [currentSessionToken]);

  // Update activity on every navigation to prevent false expiration
  useEffect(() => {
    if (user) {
      sessionManager.updateActivity();
    }
  }, [location.pathname, user]);

  // Role-based Redirect: Re-check after user enrichment to ensure users
  // are in the correct zone. Only redirect on initial load—not after the user
  // explicitly navigates to a valid zone.
  useEffect(() => {
    // Don't redirect while loading or if no user
    if (isLoading || !user) {
      return;
    }

    // Don't redirect during auth flows (login, register, verification)
    if (location.pathname.startsWith('/login') || 
        location.pathname.startsWith('/register') || 
        location.pathname.startsWith('/verify') ||
        location.pathname.startsWith('/pricing') ||
        location.pathname === '/') {
      return;
    }

    // Extract user roles
    const userRoles = extractUserRoles(user);
    const accountType = (user.user_metadata?.account_type ?? user.app_metadata?.account_type) as string | undefined;
    
    // Check user type
    const isAdmin = userRoles.some(role => ADMIN_ROLES.includes(role));
    const isPartner = accountType === 'partner' || userRoles.some(r => r.startsWith('partner_'));
    const isEnterprise = accountType === 'institution' || userRoles.some(r => r.startsWith('org_'));

    // Only redirect if the user is on the WRONG default route (/app) and should
    // be somewhere else. Never pull someone OUT of a valid zone they navigated to.
    const currentZone = location.pathname.startsWith('/admin') ? 'admin'
      : location.pathname.startsWith('/partner') ? 'partner'
      : location.pathname.startsWith('/enterprise') ? 'enterprise'
      : location.pathname.startsWith('/app') ? 'app'
      : null;

    if (!currentZone) return; // Public page, no redirect needed

    if (isAdmin && currentZone !== 'admin') {
      // Admins can access any zone — respect their zone selection
      const selectedZone = getAdminSelectedZone();
      if (currentZone === selectedZone || currentZone === 'partner' || currentZone === 'enterprise' || currentZone === 'app') {
        console.log('[SessionProvider] Admin in permitted zone, not redirecting:', location.pathname);
        return;
      }
      console.log('[SessionProvider] Admin redirecting from', location.pathname, 'to', `/${selectedZone}`);
      navigate(`/${selectedZone}`, { replace: true });
    } else if (isPartner && !isAdmin && currentZone === 'app') {
      // Partner stuck on /app — push them to /partner
      console.log('[SessionProvider] Partner redirecting from /app to /partner');
      navigate('/partner', { replace: true });
    } else if (isEnterprise && !isAdmin && !isPartner && currentZone === 'app') {
      console.log('[SessionProvider] Enterprise redirecting from /app to /enterprise');
      navigate('/enterprise', { replace: true });
    }
  }, [user, isLoading, location.pathname, navigate]);

  useEffect(() => {
    let mounted = true;

    const initialize = async () => {
      setIsLoading(true);
      try {
        // First, try to get current session from Supabase
        const { data: { session }, error } = await supabase.auth.getSession();

        if (error) {
          console.error("Session init error:", error);
        }

        const currentUser = session?.user ?? null;

        if (!mounted) return;

        // Enrich user with roles from profiles table (single source of truth)
        const enrichedUser = await enrichUserWithProfileRoles(currentUser);

        setUser(enrichedUser);

        // Initialize CSRF token and secure session if user exists
        if (enrichedUser) {
          startSecureSession();
          setCsrfToken(getCsrfToken());
          registerKnownDevice(enrichedUser.id);

          // Check if user is admin for stricter session timeout
          const userRoles = extractUserRoles(enrichedUser);
          const isAdmin = userRoles.includes('platform_admin');

          // Initialize session manager with persistent login
          sessionManager.initialize(isAdmin);

          // Set Sentry user context
          setSentryUser({
            id: enrichedUser.id,
            email: enrichedUser.email,
            accountType: enrichedUser.user_metadata?.account_type,
          });

          logSecurityEvent({
            type: "login_success",
            userId: enrichedUser.id,
            email: enrichedUser.email,
            metadata: { source: "session_init" },
          });

          // Load demo mode status for this user using secure function
          setDemoModeLoading(true);
          try {
            // Try to use the secure get_user_demo_mode function first
            const { data: demoResult, error: rpcError } = await supabase.rpc('get_user_demo_mode', {
              user_id: enrichedUser.id,
            });
            
            if (rpcError && rpcError.code !== '42883') {
              // If function doesn't exist, fall back to direct table query
              console.warn('Demo mode function not available, using table query:', rpcError.message);
              const { data: profile, error: tableError } = await supabase
                .from("profiles")
                .select("demo_mode_enabled")
                .eq("id", enrichedUser.id)
                .single();
              
              if (mounted) {
                setDemoModeEnabled(profile?.demo_mode_enabled ?? false);
              }
            } else if (demoResult) {
              if (mounted) {
                setDemoModeEnabled((demoResult as Record<string, unknown>).demo_mode_enabled === true);
              }
            } else {
              if (mounted) {
                setDemoModeEnabled(false);
              }
            }
          } catch (demoErr) {
            console.error("Error loading demo mode status:", demoErr);
            if (mounted) {
              setDemoModeEnabled(false);
            }
          } finally {
            if (mounted) {
              setDemoModeLoading(false);
            }
          }
        } else {
          // No user, demo mode is off
          setDemoModeEnabled(false);
          setDemoModeLoading(false);
        }
      } catch (err) {
        console.error("Session init error:", err);
        if (mounted) {
          setUser(null);
          setDemoModeEnabled(false);
          setDemoModeLoading(false);
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    initialize();

    const { data: subscription } = supabase.auth.onAuthStateChange((event, session) => {
      const nextUser = session?.user ?? null;

      switch (event) {
        case "SIGNED_IN": {
          if (nextUser) {
            // Enrich user with DB roles, then set state for the whole app
            (async () => {
              try {
                console.log('[SessionProvider SIGNED_IN] User signed in:', {
                  email: nextUser.email,
                  id: nextUser.id,
                  userMetadataInit: nextUser.user_metadata,
                  appMetadataInit: nextUser.app_metadata,
                });

                // CRITICAL: Verify that the Supabase session actually persisted
                // This prevents redirecting to protected routes when auth is invalid
                const validSession = await supabase.auth.getSession();
                if (!validSession.data.session) {
                  console.error('[SessionProvider] Session validation failed - no active Supabase session');
                  // Session didn't actually persist, sign out and redirect to login
                  await supabase.auth.signOut();
                  if (mounted) navigate('/login', { replace: true });
                  return;
                }
                console.log('[SessionProvider] Session validated - Supabase session exists');

                const enrichedUser = await enrichUserWithProfileRoles(nextUser);

                console.log('[SessionProvider SIGNED_IN] User enriched with profile roles:', {
                  email: enrichedUser?.email,
                  roles: enrichedUser?.user_metadata?.roles || enrichedUser?.app_metadata?.roles,
                  accountType: enrichedUser?.user_metadata?.account_type || enrichedUser?.app_metadata?.account_type,
                });

                // Update global user state with enriched user
                if (mounted) {
                  setUser(enrichedUser);
                }

                // Continue with the rest of the flow...
                // (registration check will happen naturally)

                const userRoles = extractUserRoles(enrichedUser);
                console.log('[SessionProvider] SIGNED_IN - extracted roles:', userRoles, 'account_type:', enrichedUser?.user_metadata?.account_type);

                // Register device and log security event
                registerKnownDevice(enrichedUser!.id);
                const isKnown = isKnownDevice(enrichedUser!.id);

                const isAdmin = userRoles.includes('platform_admin');
                sessionManager.initialize(isAdmin);

                setSentryUser({
                  id: enrichedUser!.id,
                  email: enrichedUser!.email,
                  accountType: enrichedUser!.user_metadata?.account_type,
                });

                logSecurityEvent({
                  type: "login_success",
                  userId: enrichedUser!.id,
                  email: enrichedUser!.email,
                  metadata: { isNewDevice: !isKnown },
                });

                // Start secure session with CSRF token
                startSecureSession();
                setCsrfToken(getCsrfToken());

                // Create integrity session tracking
                const deviceFingerprint = generateDeviceFingerprint();
                const supabaseSessionToken = session?.access_token || crypto.randomUUID();

                createSession(enrichedUser!.id, supabaseSessionToken, deviceFingerprint).then(result => {
                  if (result.success && result.sessionId) {
                    setCurrentSessionToken(supabaseSessionToken);
                    getUserSessions(enrichedUser!.id).then(activeSessions => {
                      const currentSession = activeSessions.find(s => s.sessionToken === supabaseSessionToken);
                      if (currentSession && activeSessions.length > 1) {
                        const hasOverlap = activeSessions.some(s =>
                          s.id !== currentSession.id &&
                          s.geoCountry && currentSession.geoCountry &&
                          s.geoCountry !== currentSession.geoCountry
                        );
                        if (hasOverlap) {
                          recordAnomaly(enrichedUser!.id, currentSession.id, "overlapping_session", "low", {
                            device_id: deviceFingerprint.deviceId,
                          });
                        }
                      }
                    });
                  }
                }).catch(console.error);

                if (!isKnown) {
                  toast({
                    title: "New device detected",
                    description: "You're signing in from a new device. We've registered it for future logins.",
                  });
                }

                // Load demo mode status
                setDemoModeLoading(true);
                try {
                  const { data: demoProfile } = await supabase
                    .from("profiles")
                    .select("demo_mode_enabled")
                    .eq("id", enrichedUser!.id)
                    .single();
                  if (mounted) setDemoModeEnabled(demoProfile?.demo_mode_enabled ?? false);
                } catch { if (mounted) setDemoModeEnabled(false); }
                finally { if (mounted) setDemoModeLoading(false); }

                toast({ title: "Signed in", description: "Welcome back." });

                // ---- REDIRECT LOGIC ----
                // If LoginPage already stored a destination, use it (it already enriched the user).
                // Otherwise compute a fresh redirect (e.g. OAuth callback or session restore).
                const saved = sessionStorage.getItem('postAuthRedirect');
                if (saved && saved.startsWith('/')) {
                  console.log('[SessionProvider] Using LoginPage redirect:', saved);
                  sessionStorage.removeItem('postAuthRedirect');
                  navigate(saved, { replace: true });
                  return;
                }

                // Fresh redirect: compute with enriched user
                const baseDest = computePostLoginRedirect(undefined, enrichedUser);
                console.log('[SessionProvider] Computed fresh redirect:', baseDest);

                // Subscription enforcement for non-admin users
                try {
                  if (isAdmin) { navigate(baseDest, { replace: true }); return; }

                  const accountType = enrichedUser?.user_metadata?.account_type || enrichedUser?.app_metadata?.account_type;
                  const enforceRoles = ['individual', 'consumer', 'partner', 'institution'];
                  if (enforceRoles.includes(String(accountType))) {
                    const billingMod = await import('@/lib/payments/billing');
                    const currentSub = await billingMod.billingSubscription.getCurrent();
                    if (!currentSub) {
                      navigate(`/pricing?role=${encodeURIComponent(String(accountType))}`, { replace: true });
                      return;
                    }
                    const periodEnd = new Date(currentSub.current_period_end);
                    if (periodEnd <= new Date()) {
                      toast({ title: "Subscription Expired", description: "Please renew to continue.", variant: "destructive" });
                      navigate(`/pricing?role=${encodeURIComponent(String(accountType))}`, { replace: true });
                      return;
                    }
                  }
                  navigate(baseDest, { replace: true });
                } catch (subscriptionErr) {
                  console.error("Subscription check failed:", subscriptionErr);
                  navigate(baseDest, { replace: true });
                }

              } catch (err) {
                console.error('[SessionProvider] SIGNED_IN enrichment error:', err);
                if (mounted) setUser(nextUser);
                navigate(computePostLoginRedirect(undefined, nextUser), { replace: true });
              }
            })();
          }
          break;
        }

        case "SIGNED_OUT": {
          // Clear secure session
          endSecureSession();
          setCsrfToken(null);
          setDemoModeEnabled(false);
          setDemoModeLoading(false);
          clearAdminSelectedZone();
          setAdminZone('admin');

          // Cleanup session manager
          sessionManager.cleanup();

          // End integrity session
          if (currentSessionToken) {
            endSession(currentSessionToken).catch(console.error);
            setCurrentSessionToken(null);
          }

          // Clear Sentry user context
          setSentryUser(null);

          logSecurityEvent({
            type: "logout",
            userId: user?.id,
            email: user?.email,
          });

          toast({ title: "Signed out", description: "You have been signed out." });

          // Check if there's a saved return destination
          const returnTo = sessionManager.getReturnDestination();

          // If a protected route, redirect to login with return destination
          if (location.pathname.startsWith("/app") || location.pathname.startsWith("/partner") || location.pathname.startsWith("/admin") || location.pathname.startsWith("/enterprise")) {
            // Don't save demo routes as return destinations
            if (!location.pathname.includes("/demo")) {
              sessionManager.saveReturnDestination(location.pathname);
            }
            navigate("/login");
          } else if (returnTo) {
            // If we have a saved destination and we're not on a protected route, go home
            navigate("/");
          }
          break;
        }

        case "PASSWORD_RECOVERY":
          logSecurityEvent({
            type: "password_reset",
            email: session?.user?.email,
          });
          break;

        case "TOKEN_REFRESHED": {
          // Refresh CSRF token on session refresh
          const newToken = generateCsrfToken();
          setCsrfToken(newToken);
          break;
        }
      }
    });

    return () => {
      mounted = false;
      subscription?.subscription?.unsubscribe?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, location.pathname]);

  const signOutHandler = async () => {
    await supabase.auth.signOut();
  };

  const refreshSessionHandler = async () => {
    // Refresh session via Supabase SDK
    const { data: { session }, error } = await supabase.auth.refreshSession();
    if (error) throw error;
    setUser(session?.user ?? null);

    // Refresh CSRF token on session refresh
    const match = document.cookie.match(/(?:^|; )aegis_csrf=([^;]+)/);
    if (match) setCsrfToken(decodeURIComponent(match[1]));
  };

  // Compute isAdmin for context consumers
  const userIsAdmin = user ? isAdminUser(extractUserRoles(user)) : false;

  return (
    <SessionContext.Provider value={{ user, isLoading, signOut: signOutHandler, refreshSession: refreshSessionHandler, csrfToken, demoModeEnabled, demoModeLoading, adminSelectedZone, setAdminSelectedZone: handleSetAdminZone, isAdmin: userIsAdmin }}>
      {isLoading ? (
        <InitialLoadingScreen message="Authenticating your session..." />
      ) : (
        children
      )}
    </SessionContext.Provider>
  );
}

export default SessionProvider;
